import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import "./ShopPage.css";
import Loader from "../layouts/components/Loader";
import { toast } from "react-hot-toast";
import AuthModal from "../layouts/components/AuthModal";

const API_BASE = "https://api.mariamlyluxe.com/public";

// token
const getToken = () =>
  localStorage.getItem("token") ||
  localStorage.getItem("auth_token") ||
  localStorage.getItem("access_token") ||
  "";

// fetch helper
async function apiFetch(path, { method = "GET", body } = {}) {
  const headers = { Accept: "application/json" };
  const tok = getToken();
  if (tok) headers.Authorization = `Bearer ${tok}`;
  const opts = { method, headers };
  if (body !== undefined) {
    headers["Content-Type"] = "application/json";
    opts.body = JSON.stringify(body);
  }
  const res = await fetch(`${API_BASE}${path}`, opts);
  let data = null;
  try {
    data = await res.json();
  } catch {}
  if (!res.ok) {
    const err = new Error(data?.message || `Request failed: ${res.status}`);
    err.status = res.status;
    err.errors = data?.errors;
    throw err;
  }
  return data;
}

function formatPrice(n) {
  const val = Number(n) || 0;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }).format(val);
}

function discountPct(regular, selling) {
  const r = Number(regular);
  const s = Number(selling);
  if (!r || !s || s >= r) return 0;
  return Math.round(((r - s) / r) * 100);
}

function CartIcon({
  size = 18,
  strokeWidth = 1.8,
  color = "#fff",
}) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      {/* PLUS above cart */}
      <line
        x1="12" y1="4" x2="12" y2="8"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
      />
      <line
        x1="10" y1="6" x2="14" y2="6"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
      />

      {/* Cart outline */}
      <path
        d="M6 8h13l-1.2 6.5a2 2 0 0 1-2 1.5H8a2 2 0 0 1-2-1.5L4 4H2"
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {/* Wheels */}
      <circle cx="9" cy="20" r="1.6" fill={color} />
      <circle cx="18" cy="20" r="1.6" fill={color} />
    </svg>
  );
}

function EyeIcon({
  size = 18,
  strokeWidth = 1.8,
  color = "#fff",
}) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle
        cx="12"
        cy="12"
        r="3"
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const stripDiacritics = (s) =>
  String(s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "");

const generateQueryVariants = (input) => {
  const base = String(input || "").trim();
  if (!base) return [];
  const lower = base.toLowerCase();
  const noApos = lower.replace(/[’']/g, "");
  const noPunct = lower.replace(/[^a-z0-9\s]/gi, " ");
  const noDiac = stripDiacritics(lower);
  let apostropheVariant = null;
  const m = noDiac.match(/^\s*([a-z])\s*([a-z]{2,}.*)$/i);
  if (m && !/[’']/.test(lower)) {
    apostropheVariant = `${m[1]}'${m[2].replace(/^\s+/, "")}`;
  }
  const variants = [
    base,
    lower,
    noDiac,
    noApos,
    noPunct.replace(/\s+/g, " ").trim(),
    apostropheVariant,
  ]
    .filter(Boolean)
    .map((v) => v.trim())
    .filter((v, i, arr) => v.length >= 2 && arr.indexOf(v) === i);
  return variants;
};

export default function ShopPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // data states
  const [allProducts, setAllProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [productsError, setProductsError] = useState("");
  const [isRefreshing, setIsRefreshing] = useState(false);

  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);

  const [subcategories, setSubcategories] = useState([]);
  const [subcategoriesLoading, setSubcategoriesLoading] = useState(false);

  // filters (URL-synced)
  const [filters, setFilters] = useState({
    search: "",
    category_id: "",
    subcategory_id: "",
    brand_id: "", 
    min_price: "",
    max_price: "",
    sort: "low_to_high", // "high_to_low" | "low_to_high"
    is_trending: "",
    is_new: "",
  });

  // local UI price slider (to get the exact screenshot UX)
  const [priceMin, setPriceMin] = useState(0);
  const [priceMax, setPriceMax] = useState(1000);
  const minAllowed = 0;
  const maxAllowed = 1000;
  const minGap = 1;

  // pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);

  // filter toggle state - only for mobile
  const [filtersVisible, setFiltersVisible] = useState(false);

  // auth modal state
  const [showAuthModal, setShowAuthModal] = useState(false);

  // product quick view popup state
  const [showProductPopup, setShowProductPopup] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productDetailsLoading, setProductDetailsLoading] = useState(false);

  // banner image state
  const [bannerImage, setBannerImage] = useState(null);

  // categories
  const loadCategories = async () => {
    setCategoriesLoading(true);
    try {
      const data = await apiFetch("/api/categories");
      setCategories(Array.isArray(data) ? data : data?.categories || data?.data || []);
    } catch (e) {
      console.warn("Failed to load categories:", e);
    } finally {
      setCategoriesLoading(false);
    }
  };

  // subcategories
  const loadSubcategories = async (categoryId = null) => {
    setSubcategoriesLoading(true);
    try {
      let data;
      if (categoryId) {
        data = await apiFetch(`/api/categories/${categoryId}/subcategories`);
      } else {
        data = await apiFetch("/api/subcategories");
      }
      setSubcategories(Array.isArray(data) ? data : data?.subcategories || data?.data || []);
    } catch (e) {
      console.warn("Failed to load subcategories:", e);
    } finally {
      setSubcategoriesLoading(false);
    }
  };

  // Load all products (no filters)
  const loadAllProducts = async (isRefresh = false) => {
    if (isRefresh) {
      setIsRefreshing(true);
    } else {
      setProductsLoading(true);
    }
    setProductsError("");
    try {
      const params = new URLSearchParams();
      params.append("sort", "low_to_high");
      params.append("page", currentPage);

      const data = await apiFetch(`/api/products?${params.toString()}`);

      const list = data?.products || data?.data || data || [];
      setAllProducts(Array.isArray(list) ? list : []);

      setTotalPages(data?.last_page || data?.total_pages || 1);
      setTotalProducts(data?.total || list.length || 0);
    } catch (e) {
      setProductsError(e.message || "Failed to load products.");
      setAllProducts([]);
    } finally {
      if (isRefresh) {
        setIsRefreshing(false);
      } else {
        setProductsLoading(false);
      }
    }
  };

  // Load filtered products
  const loadFilteredProducts = async (isRefresh = false) => {
    if (isRefresh) {
      setIsRefreshing(true);
    } else {
      setProductsLoading(true);
    }
    setProductsError("");
    try {
      const baseParams = new URLSearchParams();
      baseParams.append("sort", filters.sort || "low_to_high");
      baseParams.append("page", currentPage);
      if (filters.category_id) baseParams.append("category_id", filters.category_id);
      if (filters.subcategory_id) baseParams.append("subcategory_id", filters.subcategory_id);
      if (filters.brand_id) baseParams.append("brand_id", filters.brand_id);
      if (filters.min_price) baseParams.append("min_price", filters.min_price);
      if (filters.max_price) baseParams.append("max_price", filters.max_price);
      if (filters.is_trending === "true" || filters.is_trending === "1") baseParams.append("is_trending", "1");
      if (filters.is_new === "true" || filters.is_new === "1") baseParams.append("is_new", "1");

      if (filters.search) {
        const variants = generateQueryVariants(filters.search);
        let foundList = null;
        let foundMeta = null;
        for (const v of variants) {
          const p = new URLSearchParams(baseParams);
          p.append("q", v);
          const data = await apiFetch(`/api/products/search?${p.toString()}`);
          const list = data?.products?.data || [];
          if (Array.isArray(list) && list.length > 0) {
            foundList = list;
            foundMeta = data.products || data;
            break;
          }
        }
        const finalList = foundList || [];
        setFilteredProducts(finalList);
        const meta = foundMeta || {};
        setTotalPages(meta?.last_page || meta?.total_pages || 1);
        setTotalProducts(meta?.total || finalList.length || 0);
        return;
      }

      const data = await apiFetch(`/api/products?${baseParams.toString()}`);
      const list = data?.products || data?.data || data || [];
      setFilteredProducts(Array.isArray(list) ? list : []);
      setTotalPages(data?.last_page || data?.total_pages || 1);
      setTotalProducts(data?.total || list.length || 0);
    } catch (e) {
      setProductsError(e.message || "Failed to load products.");
      setFilteredProducts([]);
    } finally {
      if (isRefresh) {
        setIsRefreshing(false);
      } else {
        setProductsLoading(false);
      }
    }
  };

  const loadBannerImage = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/pages-banners/shop`, {
        headers: { Accept: "application/json" }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.banner && data.banner.image_url) {
          setBannerImage(data.banner.image_url);
        }
      }
    } catch (e) {
      console.warn("Failed to load banner image:", e);
      // Fallback to default image if API fails
      setBannerImage('../images/crosswalk.jpg');
    }
  };

  // sync filters from URL
  useEffect(() => {
    const url = {
      search: searchParams.get("q") || searchParams.get("search") || "",
      brand_id: searchParams.get("brand") || searchParams.get("brand_id") || "", 
      category_id: searchParams.get("category_id") || "",
      subcategory_id: searchParams.get("subcategory_id") || "",
      min_price: searchParams.get("min_price") || "",
      max_price: searchParams.get("max_price") || "",
      sort: searchParams.get("sort") || "low_to_high",
      is_trending: searchParams.get("is_trending") === "1" ? "1" : "",
      is_new: searchParams.get("is_new") === "1" ? "1" : "",
    };
    setFilters(url);
    // mirror to slider if present
    if (url.min_price) setPriceMin(Number(url.min_price));
    if (url.max_price) setPriceMax(Number(url.max_price));
  }, [searchParams]);

  useEffect(() => {
    loadCategories();
    loadSubcategories();
    loadBannerImage();
  }, []);

  // Load subcategories when category changes
  useEffect(() => {
    if (filters.category_id) {
      loadSubcategories(filters.category_id);
    } else {
      loadSubcategories();
    }
  }, [filters.category_id]);

  useEffect(() => {
    // Check if any meaningful filters are applied
    const hasFilters = filters.category_id || 
                      filters.subcategory_id || 
                      filters.brand_id || 
                      filters.min_price || 
                      filters.max_price || 
                      filters.search || 
                      filters.is_trending === "true" || 
                      filters.is_trending === "1" ||
                      filters.is_new === "true" || 
                      filters.is_new === "1";

    // Check if we already have data (not initial load)
    const hasExistingData = (hasFilters && filteredProducts.length > 0) || (!hasFilters && allProducts.length > 0);

    if (hasFilters) {
      loadFilteredProducts(hasExistingData);
    } else {
      loadAllProducts(hasExistingData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, currentPage]);

  const updateURL = (next) => {
    const p = new URLSearchParams();
    Object.entries(next).forEach(([k, v]) => {
      if (v !== "" && v !== null && v !== undefined) p.set(k, v);
    });
    setSearchParams(p);
  };

  const handleFilterChange = (key, value) => {
    const next = { ...filters, [key]: value };
    setFilters(next);
    setCurrentPage(1);
    updateURL(next);
  };

  // exact screenshot search box
  const onSearchSubmit = (e) => {
    e.preventDefault();
    handleFilterChange("search", filters.search);
  };

  // Submit button: commit the slider values to real filters
  const submitFilters = () => {
    const next = {
      ...filters,
      min_price: priceMin || "",
      max_price: priceMax || "",
    };
    setFilters(next);
    setCurrentPage(1);
    updateURL(next);
  };

  const clearAllFilters = () => {
    const clearedFilters = {
      search: "",
      category_id: "",
      subcategory_id: "",
      brand_id: "",
      min_price: "",
      max_price: "",
      sort: "low_to_high",
      is_trending: "",
      is_new: "",
    };
    setFilters(clearedFilters);
    setCurrentPage(1);
    setPriceMin(0);
    setPriceMax(1000);
    updateURL(clearedFilters);
  };

  const clearCategory = () => {
    const next = { ...filters, category_id: "", subcategory_id: "" };
    setFilters(next);
    setCurrentPage(1);
    updateURL(next);
  };
  const clearSubcategory = () => handleFilterChange("subcategory_id", "");

  // cart loading states
  const [cartLoading, setCartLoading] = useState({});

  // add to cart functionality
  const handleAddToCart = async (product) => {
    const productId = product.id;
    const token = getToken();
    
    if (!token) {
      setShowAuthModal(true);
      return;
    }
    
    // Set loading state for this specific product
    setCartLoading(prev => ({ ...prev, [productId]: true }));
    
    try {
      const response = await apiFetch('/api/cart', {
        method: 'POST',
        body: { 
          product_id: productId, 
          quantity: 1 
        }
      });
      
      // Show success toast
      toast.success(`${product.name} added to cart successfully!`);
      
    } catch (error) {
      console.error('Failed to add to cart:', error);
      
      // Show error toast
      toast.error('Failed to add item to cart. Please try again.');
    } finally {
      // Clear loading state
      setCartLoading(prev => ({ ...prev, [productId]: false }));
    }
  };

  // Handle successful login from AuthModal
  const handleLoginSuccess = () => {
    setShowAuthModal(false);
    // Optionally refresh data or update UI state after login
  };

  // Handle product quick view
  const handleProductQuickView = async (product) => {
    setShowProductPopup(true);
    setProductDetailsLoading(true);
    setSelectedProduct(null);
    
    try {
      const data = await apiFetch(`/api/products/${product.id}`);
      if (data.success && data.product) {
        setSelectedProduct(data.product);
      } else {
        toast.error('Failed to load product details');
        setShowProductPopup(false);
      }
    } catch (error) {
      console.error('Failed to fetch product details:', error);
      toast.error('Failed to load product details');
      setShowProductPopup(false);
    } finally {
      setProductDetailsLoading(false);
    }
  };

  // Close product popup
  const closeProductPopup = () => {
    setShowProductPopup(false);
    setSelectedProduct(null);
    setProductDetailsLoading(false);
  };

  // PRICE SLIDER guards
  const onMinChange = (v) => {
    const n = Math.min(Number(v), priceMax - minGap);
    setPriceMin(Math.max(n, minAllowed));
  };
  const onMaxChange = (v) => {
    const n = Math.max(Number(v), priceMin + minGap);
    setPriceMax(Math.min(n, maxAllowed));
  };

  return (
    <section className="shop-page">
      {/* Banner Section */}
      <div 
        className="shop-banner"
        style={{
          backgroundImage: bannerImage ? `url(${bannerImage})` : 'url(../images/crosswalk.jpg)'
        }}
      >
        <div className="shop-banner-content">
          <h1 className="shop-banner-title">Mariamly</h1>
          <nav className="shop-breadcrumb">
            <Link to="/">Mariamly</Link>
            <span className="shop-breadcrumb-separator">›</span>
            <span className="shop-breadcrumb-current">Shop</span>
          </nav>
        </div>
      </div>

      <div className="shop-container">
        <div className="shop-content">
          {/* Mobile Filter Toggle */}
          <div className="shop-mobile-filters-toggle">
            <button 
              className="shop-toggle-filters-btn"
              onClick={() => setFiltersVisible(!filtersVisible)}
              aria-label={filtersVisible ? "Hide filters" : "Show filters"}
            >
              <span>Filters</span>
              <svg 
                width="20" 
                height="20" 
                viewBox="0 0 24 24" 
                fill="none"
                className={`shop-toggle-icon ${filtersVisible ? 'expanded' : ''}`}
              >
                <path 
                  d="M6 9l6 6 6-6" 
                  stroke="#d19e90" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>

          <div className="shop-layout">
            {/* DESKTOP SIDEBAR FILTERS */}
            <aside className="shop-desktop-filters">
              {/* Search */}
              <div className="shop-filter-section">
                <form onSubmit={onSearchSubmit} className="shop-filter-search-compact">
                  <input
                    type="text"
                    placeholder="Search products"
                    value={filters.search}
                    onChange={(e) => setFilters((p) => ({ ...p, search: e.target.value }))}
                  />
                  <button type="submit" aria-label="Search">
                    {/* magnifier */}
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path d="M21 21l-4.2-4.2" stroke="#7B8494" strokeWidth="2" strokeLinecap="round" />
                      <circle cx="11" cy="11" r="7" stroke="#7B8494" strokeWidth="2" />
                    </svg>
                  </button>
                </form>
              </div>

              {/* Price */}
              <div className="shop-filter-section">
                <div className="shop-section-title-with-icon">
                  <h3>Price</h3>
                  <span className="shop-funnel-icon" aria-hidden>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M3 5h18l-7 8v5l-4 2v-7L3 5z"
                        stroke="#d19e90"
                        strokeWidth="1.5"
                        fill="none"
                      />
                    </svg>
                  </span>
                </div>

                {/* dual range track */}
                <div className="shop-range-wrap">
                  <div className="shop-slider">
                    <input
                      type="range"
                      min={minAllowed}
                      max={maxAllowed}
                      value={priceMin}
                      onChange={(e) => onMinChange(e.target.value)}
                    />
                    <input
                      type="range"
                      min={minAllowed}
                      max={maxAllowed}
                      value={priceMax}
                      onChange={(e) => onMaxChange(e.target.value)}
                    />
                    <div
                      className="shop-slider-progress"
                      style={{
                        left: `${(priceMin / (maxAllowed - minAllowed)) * 100}%`,
                        right: `${100 - (priceMax / (maxAllowed - minAllowed)) * 100}%`,
                      }}
                    />
                  </div>

                  <div className="shop-range-row">
                    <span className="shop-range-label">Range</span>
                    <div className="shop-range-values">
                      <span>${priceMin}</span>
                      <span> - </span>
                      <span>${priceMax}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Categories */}
              <div className="shop-filter-section">
                <h3 className="shop-filter-title">Categories</h3>
                <div className="shop-categories-list">
                  {categoriesLoading ? (
                    <Loader />
                  ) : (
                    <>
                      <button
                        className={`shop-category-row ${!filters.category_id ? "active" : ""}`}
                        onClick={clearCategory}
                        type="button"
                      >
                        <span>All</span>
                        <i className="shop-chevron">&rsaquo;</i>
                      </button>
                      {categories.map((c) => (
                        <div key={c.id}>
                          <button
                            className={`shop-category-row ${
                              filters.category_id === String(c.id) ? "active" : ""
                            }`}
                            onClick={() => handleFilterChange("category_id", String(c.id))}
                            type="button"
                          >
                            <span>{c.name}</span>
                            <i className="shop-chevron">&rsaquo;</i>
                          </button>
                          {/* Show subcategories for this category */}
                          {filters.category_id === String(c.id) && (
                            <div className="shop-subcategories-list">
                              {subcategoriesLoading ? (
                                <div className="shop-subcategory-loading">
                                  <Loader size={16} />
                                </div>
                              ) : (
                                <>
                                  <button
                                    className={`shop-subcategory-row ${!filters.subcategory_id ? "active" : ""}`}
                                    onClick={clearSubcategory}
                                    type="button"
                                  >
                                    <span>All {c.name}</span>
                                    <i className="shop-chevron">&rsaquo;</i>
                                  </button>
                                  {subcategories.map((sc) => (
                                    <button
                                      key={sc.id}
                                      className={`shop-subcategory-row ${
                                        filters.subcategory_id === String(sc.id) ? "active" : ""
                                      }`}
                                      onClick={() => handleFilterChange("subcategory_id", String(sc.id))}
                                      type="button"
                                    >
                                      <span>{sc.name}</span>
                                      <i className="shop-chevron">&rsaquo;</i>
                                    </button>
                                  ))}
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </>
                  )}
                </div>
              </div>


              {/* Sort toggles exactly like screenshot */}
              <div className="shop-toggle-row">
                <span>Hight To Low</span>
                <label className="shop-switch">
                  <input
                    type="checkbox"
                    checked={filters.sort === "high_to_low"}
                    onChange={(e) =>
                      handleFilterChange("sort", e.target.checked ? "high_to_low" : "low_to_high")
                    }
                  />
                  <span className="shop-slider-toggle" />
                </label>
              </div>

              <div className="shop-toggle-row">
                <span>Low To Hight</span>
                <label className="shop-switch">
                  <input
                    type="checkbox"
                    checked={filters.sort === "low_to_high"}
                    onChange={(e) =>
                      handleFilterChange("sort", e.target.checked ? "low_to_high" : "high_to_low")
                    }
                  />
                  <span className="shop-slider-toggle" />
                </label>
              </div>

              <button className="shop-submit-pill" type="button" onClick={submitFilters}>
                Submit
              </button>
            </aside>

            {/* MOBILE FILTERS */}
            <div className={`shop-mobile-filters ${filtersVisible ? 'visible' : 'hidden'}`}>
              {/* Search */}
              <div className="shop-filter-section">
                <form onSubmit={onSearchSubmit} className="shop-filter-search-compact">
                  <input
                    type="text"
                    placeholder="Search products"
                    value={filters.search}
                    onChange={(e) => setFilters((p) => ({ ...p, search: e.target.value }))}
                  />
                  <button type="submit" aria-label="Search">
                    {/* magnifier */}
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path d="M21 21l-4.2-4.2" stroke="#7B8494" strokeWidth="2" strokeLinecap="round" />
                      <circle cx="11" cy="11" r="7" stroke="#7B8494" strokeWidth="2" />
                    </svg>
                  </button>
                </form>
              </div>

              {/* Price */}
              <div className="shop-filter-section">
                <div className="shop-section-title-with-icon">
                  <h3>Price</h3>
                  <span className="shop-funnel-icon" aria-hidden>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M3 5h18l-7 8v5l-4 2v-7L3 5z"
                        stroke="#d19e90"
                        strokeWidth="1.5"
                        fill="none"
                      />
                    </svg>
                  </span>
                </div>

                {/* dual range track */}
                <div className="shop-range-wrap">
                  <div className="shop-slider">
                    <input
                      type="range"
                      min={minAllowed}
                      max={maxAllowed}
                      value={priceMin}
                      onChange={(e) => onMinChange(e.target.value)}
                    />
                    <input
                      type="range"
                      min={minAllowed}
                      max={maxAllowed}
                      value={priceMax}
                      onChange={(e) => onMaxChange(e.target.value)}
                    />
                    <div
                      className="shop-slider-progress"
                      style={{
                        left: `${(priceMin / (maxAllowed - minAllowed)) * 100}%`,
                        right: `${100 - (priceMax / (maxAllowed - minAllowed)) * 100}%`,
                      }}
                    />
                  </div>

                  <div className="shop-range-row">
                    <span className="shop-range-label">Range</span>
                    <div className="shop-range-values">
                      <span>${priceMin}</span>
                      <span> - </span>
                      <span>${priceMax}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Categories */}
              <div className="shop-filter-section">
                <h3 className="shop-filter-title">Categories</h3>
                <div className="shop-categories-list">
                  {categoriesLoading ? (
                    <Loader />
                  ) : (
                    <>
                      <button
                        className={`shop-category-row ${!filters.category_id ? "active" : ""}`}
                        onClick={clearCategory}
                        type="button"
                      >
                        <span>All</span>
                        <i className="shop-chevron">&rsaquo;</i>
                      </button>
                      {categories.map((c) => (
                        <div key={c.id}>
                          <button
                            className={`shop-category-row ${
                              filters.category_id === String(c.id) ? "active" : ""
                            }`}
                            onClick={() => handleFilterChange("category_id", String(c.id))}
                            type="button"
                          >
                            <span>{c.name}</span>
                            <i className="shop-chevron">&rsaquo;</i>
                          </button>
                          {/* Show subcategories for this category */}
                          {filters.category_id === String(c.id) && (
                            <div className="shop-subcategories-list">
                              {subcategoriesLoading ? (
                                <div className="shop-subcategory-loading">
                                  <Loader size={16} />
                                </div>
                              ) : (
                                <>
                                  <button
                                    className={`shop-subcategory-row ${!filters.subcategory_id ? "active" : ""}`}
                                    onClick={clearSubcategory}
                                    type="button"
                                  >
                                    <span>All {c.name}</span>
                                    <i className="shop-chevron">&rsaquo;</i>
                                  </button>
                                  {subcategories.map((sc) => (
                                    <button
                                      key={sc.id}
                                      className={`shop-subcategory-row ${
                                        filters.subcategory_id === String(sc.id) ? "active" : ""
                                      }`}
                                      onClick={() => handleFilterChange("subcategory_id", String(sc.id))}
                                      type="button"
                                    >
                                      <span>{sc.name}</span>
                                      <i className="shop-chevron">&rsaquo;</i>
                                    </button>
                                  ))}
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </>
                  )}
                </div>
              </div>


              {/* Sort toggles exactly like screenshot */}
              <div className="shop-toggle-row">
                <span>Hight To Low</span>
                <label className="shop-switch">
                  <input
                    type="checkbox"
                    checked={filters.sort === "high_to_low"}
                    onChange={(e) =>
                      handleFilterChange("sort", e.target.checked ? "high_to_low" : "low_to_high")
                    }
                  />
                  <span className="shop-slider-toggle" />
                </label>
              </div>

              <div className="shop-toggle-row">
                <span>Low To Hight</span>
                <label className="shop-switch">
                  <input
                    type="checkbox"
                    checked={filters.sort === "low_to_high"}
                    onChange={(e) =>
                      handleFilterChange("sort", e.target.checked ? "low_to_high" : "high_to_low")
                    }
                  />
                  <span className="shop-slider-toggle" />
                </label>
              </div>

              <button className="shop-submit-pill" type="button" onClick={submitFilters}>
                Submit
              </button>
            </div>

            {/* PRODUCTS SECTION */}
            <div className="shop-products">
            <div className="shop-products-header">
              <h2>Products</h2>
              {filters.is_trending === "true" && (
                <div className="shop-filter-indicator">
                  <span className="shop-filter-badge shop-filter-badge-trending">Trending Products</span>
                </div>
              )}
              {filters.is_new === "true" && (
                <div className="shop-filter-indicator">
                  <span className="shop-filter-badge shop-filter-badge-new">New Products</span>
                </div>
              )}
            </div>

            {productsLoading ? (
              <div className="shop-products-loading">
                <Loader />
                <p>Loading products…</p>
              </div>
            ) : isRefreshing ? (
              <div className="shop-products-refreshing">
                <div className="shop-refresh-overlay">
                  <Loader />
                  <p>Updating products…</p>
                </div>
                <div className="shop-products-grid">
                  {(() => {
                    const hasFilters = filters.category_id || 
                                      filters.subcategory_id || 
                                      filters.brand_id || 
                                      filters.min_price || 
                                      filters.max_price || 
                                      filters.search || 
                                      filters.is_trending === "true" || 
                                      filters.is_trending === "1" ||
                                      filters.is_new === "true" || 
                                      filters.is_new === "1";
                    const currentProducts = hasFilters ? filteredProducts : allProducts;
                    return currentProducts.map((p) => {
                    const reg = p.regular_price || p.mrp || p.price;
                    const sell = p.selling_price || p.price;
                    const pct = discountPct(reg, sell);
                    return (
                      <div key={p.id} className="shop-product-card">
                        <Link to={`/product/${p.slug || p.id}`} className="shop-product-link">
                          <div className="shop-product-image">
                            {p.image_url ? (
                              <img src={p.image_url} alt={p.name} />
                            ) : (
                              <div className="shop-no-image">No Image</div>
                            )}
                            {/* Quick view button */}
                            <button 
                              className="shop-quick-view-btn"
                              onClick={(e) => {
                                e.preventDefault();
                                handleProductQuickView(p);
                              }}
                              aria-label="Quick view"
                              title="Quick view"
                            >
                              <EyeIcon size={20} />
                            </button>
                          </div>
                          <div className="shop-product-info">
                            <div className="shop-product-top">
                              <h3 className="shop-product-name">{p.name}</h3>
                              <button 
                                className="shop-add-to-cart-btn"
                                aria-label={cartLoading[p.id] ? "Adding..." : "Add to cart"}
                                onClick={(e) => {
                                  e.preventDefault();
                                  handleAddToCart(p);
                                }}
                                disabled={cartLoading[p.id]}
                                title={cartLoading[p.id] ? "Adding..." : "Add to cart"}
                              >
                                {cartLoading[p.id] ? <Loader size={18} /> : <CartIcon />}
                              </button>
                            </div>
                            {p.variants && Array.isArray(p.variants) && p.variants.length > 0 ? (
                              <div className="shop-product-pricing-info">
                                <div className="shop-product-pricing-left">
                                  <span className="shop-product-price">
                                    {(() => {
                                      const prices = p.variants
                                        .map(v => Number(v.selling_price || v.price || 0))
                                        .filter(p => p > 0);
                                      if (prices.length === 0) return formatPrice(sell);
                                      const minPrice = Math.min(...prices);
                                      const maxPrice = Math.max(...prices);
                                      return minPrice !== maxPrice 
                                        ? `${formatPrice(minPrice)} - ${formatPrice(maxPrice)}`
                                        : formatPrice(minPrice);
                                    })()}
                                  </span>
                                </div>
                              </div>
                            ) : (
                              <div className="shop-product-pricing-info">
                                <div className="shop-product-pricing-left">
                                  {reg && Number(reg) !== Number(sell) && (
                                    <span className="shop-product-original-price" style={{ color: '#e74c3c', textDecoration: 'line-through' }}>{formatPrice(reg)}</span>
                                  )}
                                  <span className="shop-product-price">{formatPrice(sell)}</span>
                                </div>
                                {reg && Number(reg) !== Number(sell) && (
                                  <span className="shop-product-savings">
                                    Save {discountPct(reg, sell)}%  
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </Link>
                      </div>
                    );
                    });
                  })()}
                </div>
              </div>
            ) : productsError ? (
              <div className="shop-products-error">
                <p>{productsError}</p>
                <button onClick={() => {
                  const hasFilters = filters.category_id || 
                                    filters.subcategory_id || 
                                    filters.brand_id || 
                                    filters.min_price || 
                                    filters.max_price || 
                                    filters.search || 
                                    filters.is_trending === "true" || 
                                    filters.is_trending === "1" ||
                                    filters.is_new === "true" || 
                                    filters.is_new === "1";
                  if (hasFilters) {
                    loadFilteredProducts();
                  } else {
                    loadAllProducts();
                  }
                }}>Try Again</button>
              </div>
            ) : (() => {
              const hasFilters = filters.category_id || 
                                filters.subcategory_id || 
                                filters.brand_id || 
                                filters.min_price || 
                                filters.max_price || 
                                filters.search || 
                                filters.is_trending === "true" || 
                                filters.is_trending === "1" ||
                                filters.is_new === "true" || 
                                filters.is_new === "1";
              const currentProducts = hasFilters ? filteredProducts : allProducts;
              return currentProducts.length === 0;
            })() ? (
              <div className="shop-products-empty">
                <div className="shop-empty-state-content">
                  <div className="shop-empty-state-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <h3 className="shop-empty-state-title">No Products Found</h3>
                  <p className="shop-empty-state-description">
                    We couldn't find any products matching your current filters. 
                    Try adjusting your search criteria or browse our full collection.
                  </p>
                  <div className="shop-empty-state-actions">
                    <button 
                      className="shop-btn-primary"
                      onClick={() => {
                        setFilters({
                          search: "",
                          category_id: "",
                          subcategory_id: "",
                          brand_id: "",
                          min_price: "",
                          max_price: "",
                          sort: "low_to_high",
                          is_trending: "",
                          is_new: "",
                        });
                        setCurrentPage(1);
                        updateURL({
                          search: "",
                          category_id: "",
                          subcategory_id: "",
                          brand_id: "",
                          min_price: "",
                          max_price: "",
                          sort: "low_to_high",
                          is_trending: "",
                          is_new: "",
                        });
                      }}
                    >
                      Clear All Filters
                    </button>
                    <button 
                      className="shop-btn-secondary"
                      onClick={() => navigate('/')}
                    >
                      Browse Homepage
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className="shop-products-grid">
                  {(() => {
                    const hasFilters = filters.category_id || 
                                      filters.subcategory_id || 
                                      filters.brand_id || 
                                      filters.min_price || 
                                      filters.max_price || 
                                      filters.search || 
                                      filters.is_trending === "true" || 
                                      filters.is_trending === "1" ||
                                      filters.is_new === "true" || 
                                      filters.is_new === "1";
                    const currentProducts = hasFilters ? filteredProducts : allProducts;
                    return currentProducts.map((p) => {
                    const reg = p.regular_price || p.mrp || p.price;
                    const sell = p.selling_price || p.price;
                    const pct = discountPct(reg, sell);
                    return (
                      <div key={p.id} className="shop-product-card">
                        <Link to={`/product/${p.slug || p.id}`} className="shop-product-link">
                          <div className="shop-product-image">
                            {p.image_url ? (
                              <img src={p.image_url} alt={p.name} />
                            ) : (
                              <div className="shop-no-image">No Image</div>
                            )}
                            {/* Quick view button */}
                            <button 
                              className="shop-quick-view-btn"
                              onClick={(e) => {
                                e.preventDefault();
                                handleProductQuickView(p);
                              }}
                              aria-label="Quick view"
                              title="Quick view"
                            >
                              <EyeIcon size={20} />
                            </button>
                          </div>
                          <div className="shop-product-info">
                            <div className="shop-product-top">
                              <h3 className="shop-product-name">{p.name}</h3>
                              <button 
                                className="shop-add-to-cart-btn"
                                aria-label={cartLoading[p.id] ? "Adding..." : "Add to cart"}
                                onClick={(e) => {
                                  e.preventDefault();
                                  handleAddToCart(p);
                                }}
                                disabled={cartLoading[p.id]}
                                title={cartLoading[p.id] ? "Adding..." : "Add to cart"}
                              >
                                {cartLoading[p.id] ? <Loader size={18} /> : <CartIcon />}
                              </button>
                            </div>
                            {p.variants && Array.isArray(p.variants) && p.variants.length > 0 ? (
                              <div className="shop-product-pricing-info">
                                <div className="shop-product-pricing-left">
                                  <span className="shop-product-price">
                                    {(() => {
                                      const prices = p.variants
                                        .map(v => Number(v.selling_price || v.price || 0))
                                        .filter(p => p > 0);
                                      if (prices.length === 0) return formatPrice(sell);
                                      const minPrice = Math.min(...prices);
                                      const maxPrice = Math.max(...prices);
                                      return minPrice !== maxPrice 
                                        ? `${formatPrice(minPrice)} - ${formatPrice(maxPrice)}`
                                        : formatPrice(minPrice);
                                    })()}
                                  </span>
                                </div>
                              </div>
                            ) : (
                              <div className="shop-product-pricing-info">
                                <div className="shop-product-pricing-left">
                                  {reg && Number(reg) !== Number(sell) && (
                                    <span className="shop-product-original-price" style={{ color: '#e74c3c', textDecoration: 'line-through' }}>{formatPrice(reg)}</span>
                                  )}
                                  <span className="shop-product-price">{formatPrice(sell)}</span>
                                </div>
                                {reg && Number(reg) !== Number(sell) && (
                                  <span className="shop-product-savings">
                                    Save {discountPct(reg, sell)}%  
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </Link>
                      </div>
                    );
                    });
                  })()}
                </div>

                {totalPages > 1 && (
                  <div className="shop-pagination">
                    <button
                      className="shop-pagination-btn"
                      onClick={() => setCurrentPage((v) => Math.max(1, v - 1))}
                      disabled={currentPage === 1}
                    >
                      &lt;
                    </button>
                    <span className="shop-pagination-info">
                      {currentPage} of {totalPages}
                    </span>
                    <button
                      className="shop-pagination-btn"
                      onClick={() => setCurrentPage((v) => Math.min(totalPages, v + 1))}
                      disabled={currentPage === totalPages}
                    >
                      &gt;
                    </button>
                  </div>
                )}
              </>
            )}
            </div>
          </div>
        </div>
      </div>

      {/* AuthModal component */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onLoginSuccess={handleLoginSuccess}
      />

      {/* Product Quick View Popup */}
      {showProductPopup && (
        <div className="shop-product-popup-overlay" onClick={closeProductPopup}>
          <div className="shop-product-popup" onClick={(e) => e.stopPropagation()}>
            <button 
              className="shop-popup-close"
              onClick={closeProductPopup}
              aria-label="Close popup"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path 
                  d="M18 6L6 18M6 6l12 12" 
                  stroke="#666" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            
            {productDetailsLoading ? (
              <div className="shop-popup-loading">
                <Loader />
                <p>Loading product details...</p>
              </div>
            ) : selectedProduct ? (
              <div className="shop-popup-content">
              <div className="shop-popup-image">
                {selectedProduct.image_url ? (
                  <img src={selectedProduct.image_url} alt={selectedProduct.name} />
                ) : (
                  <div className="shop-popup-no-image">No Image</div>
                )}
              </div>
              
              <div className="shop-popup-details">
                <div className="shop-popup-header">
                  <h2 className="shop-popup-title">{selectedProduct.name}</h2>
                  {selectedProduct.sku && (
                    <span className="shop-popup-sku">SKU: {selectedProduct.sku}</span>
                  )}
                </div>
                
                {/* Brand and Categories */}
                <div className="shop-popup-meta">
                  {selectedProduct.brand && (
                    <div className="shop-popup-meta-item">
                      <span className="shop-popup-meta-label">Brand:</span>
                      <span className="shop-popup-meta-value">{selectedProduct.brand.name}</span>
                    </div>
                  )}
                  {selectedProduct.categories && selectedProduct.categories.length > 0 && (
                    <div className="shop-popup-meta-item">
                      <span className="shop-popup-meta-label">Category:</span>
                      <span className="shop-popup-meta-value">
                        {selectedProduct.categories.map(cat => cat.name).join(', ')}
                      </span>
                    </div>
                  )}
                </div>
                
                {/* Description */}
                {(selectedProduct.desc || selectedProduct.description) && (
                  <div className="shop-popup-section">
                    <h4 className="shop-popup-section-title">Description</h4>
                    <p className="shop-popup-description">
                      {selectedProduct.desc || selectedProduct.description}
                    </p>
                  </div>
                )}
                
                {/* Product Details */}
                <div className="shop-popup-section">
                  <h4 className="shop-popup-section-title">Product Details</h4>
                  <div className="shop-popup-details-grid">
                    {selectedProduct.weight && (
                      <div className="shop-popup-detail-item">
                        <span className="shop-popup-detail-label">Weight:</span>
                        <span className="shop-popup-detail-value">{selectedProduct.weight}</span>
                      </div>
                    )}
                    {selectedProduct.ingredients && (
                      <div className="shop-popup-detail-item shop-popup-detail-full">
                        <span className="shop-popup-detail-label">Ingredients:</span>
                        <span className="shop-popup-detail-value">{selectedProduct.ingredients}</span>
                      </div>
                    )}
                    {selectedProduct.usage_instructions && (
                      <div className="shop-popup-detail-item shop-popup-detail-full">
                        <span className="shop-popup-detail-label">Usage Instructions:</span>
                        <span className="shop-popup-detail-value">{selectedProduct.usage_instructions}</span>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Variants */}
                {selectedProduct.variants && selectedProduct.variants.length > 0 && (
                  <div className="shop-popup-section">
                    <h4 className="shop-popup-section-title">Available Variants</h4>
                    <div className="shop-popup-variants">
                      {selectedProduct.variants.map((variant) => (
                        <div key={variant.id} className="shop-popup-variant">
                          <div 
                            className="shop-popup-variant-color"
                            style={{ backgroundColor: variant.hex_color }}
                            title={variant.color}
                          ></div>
                          <div className="shop-popup-variant-info">
                            <span className="shop-popup-variant-name">{variant.color}</span>
                            <span className="shop-popup-variant-price">
                              {formatPrice(variant.selling_price || variant.price || 0)}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Pricing */}
                <div className="shop-popup-pricing">
                  {(() => {
                    // If product has variants, show price range
                    if (selectedProduct.variants && selectedProduct.variants.length > 0) {
                      const prices = selectedProduct.variants
                        .map(v => Number(v.selling_price || v.price || 0))
                        .filter(p => p > 0);
                      
                      if (prices.length > 0) {
                        const minPrice = Math.min(...prices);
                        const maxPrice = Math.max(...prices);
                        const displayPrice = minPrice !== maxPrice 
                          ? `${formatPrice(minPrice)} - ${formatPrice(maxPrice)}`
                          : formatPrice(minPrice);
                        
                        return <span className="shop-popup-price">{displayPrice}</span>;
                      }
                    }
                    
                    // Regular pricing for products without variants
                    const reg = selectedProduct.regular_price || selectedProduct.mrp || selectedProduct.price;
                    const sell = selectedProduct.selling_price || selectedProduct.price;
                    const pct = discountPct(reg, sell);
                    
                    return (
                      <>
                        {reg && Number(reg) !== Number(sell) && (
                          <span className="shop-popup-original-price">
                            {formatPrice(reg)}
                          </span>
                        )}
                        <span className="shop-popup-price">{formatPrice(sell)}</span>
                        {reg && Number(reg) !== Number(sell) && (
                          <span className="shop-popup-discount">
                            Save {pct}%
                          </span>
                        )}
                      </>
                    );
                  })()}
                </div>
                
                {/* Actions */}
                <div className="shop-popup-actions">
                  <button 
                    className="shop-popup-view-btn"
                    onClick={() => {
                      closeProductPopup();
                      navigate(`/product/${selectedProduct.slug || selectedProduct.id}`);
                    }}
                  >
                    View Full Details
                  </button>
                  
                  
                </div>
              </div>
            </div>
            ) : (
              <div className="shop-popup-error">
                <p>Failed to load product details</p>
                <button onClick={closeProductPopup}>Close</button>
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
